"use client"

import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"
import {
  parseStack,
  getSuspectFrame,
  formatFrame,
  isStackLikelyMapped,
  type StackFrame,
} from "@/lib/crash-locator"

const CRASH_PERSIST_KEY = "crash-locator-persisting"

/** Dev-only: persist crash to Firestore. Guarded against recursion. Exported for global-error/error boundaries. */
export async function persistCrashReport(payload: {
  message: string
  stack: string | undefined
  route: string
  buildId: string
  suspect: string | null
  frames: string[]
  userId: string | null
}): Promise<string | null> {
  if (process.env.NODE_ENV !== "development") return null
  if ((window as unknown as Record<string, unknown>)[CRASH_PERSIST_KEY]) return null
  ;(window as unknown as Record<string, unknown>)[CRASH_PERSIST_KEY] = true
  try {
    const res = await fetch("/api/debug/crash-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
    if (!res.ok) return null
    const data = (await res.json()) as { id?: string }
    return data.id ?? null
  } finally {
    ;(window as unknown as Record<string, unknown>)[CRASH_PERSIST_KEY] = false
  }
}

export const CRASH_REPORT_EVENT = "crash-locator-report"

export interface CrashReport {
  message: string
  stack: string | undefined
  route: string
  buildId: string
  frames: StackFrame[]
  suspect: ReturnType<typeof getSuspectFrame>
  suspectFormatted: string | null
  sourcemapWarn: boolean
  timestamp: number
  /** Set after persisting to Firestore (dev only). */
  documentId?: string | null
}

function getBuildId(): string {
  if (typeof window === "undefined") return "ssr"
  const nextData = (window as unknown as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__
  return nextData?.buildId ?? "unknown"
}

function logCrashReport(report: CrashReport): void {
  const { message, stack, route, buildId, suspect, suspectFormatted, sourcemapWarn } = report
  console.error(
    "[CRASH LOCATOR]",
    "\n  message:",
    message,
    "\n  route:",
    route,
    "\n  buildId:",
    buildId,
    "\n  suspect:",
    suspectFormatted ?? "(none)",
    "\n  full stack:\n",
    stack ?? "(no stack)"
  )
  if (sourcemapWarn) {
    console.warn("[CRASH LOCATOR] Sourcemap may not be applied — stack has no app/lib/components paths. Check .map serving.")
  }
}

/**
 * Client component that provides current route to ErrorCapture. Mount once in root client layout.
 */
export function ErrorCaptureWithRoute() {
  const pathname = usePathname()
  return <ErrorCapture route={pathname ?? window?.location?.pathname ?? ""} />
}

export function ErrorCapture({ route }: { route: string }) {
  const routeRef = useRef(route)
  routeRef.current = route

  useEffect(() => {
    const buildId = getBuildId()

    const buildReport = (message: string, stack: string | undefined): CrashReport => {
      const frames = parseStack(stack)
      const suspect = getSuspectFrame(stack)
      const suspectFormatted = suspect ? formatFrame(suspect) : null
      const sourcemapWarn = !isStackLikelyMapped(stack)
      return {
        message,
        stack,
        route: routeRef.current,
        buildId,
        frames,
        suspect,
        suspectFormatted,
        sourcemapWarn,
        timestamp: Date.now(),
      }
    }

    const dispatchReport = (report: CrashReport, documentId?: string | null) => {
      const withId = documentId != null ? { ...report, documentId } : report
      logCrashReport(withId)
      window.dispatchEvent(
        new CustomEvent(CRASH_REPORT_EVENT, { detail: withId })
      )
    }

    const onError = (event: ErrorEvent) => {
      const err = event.error ?? { message: event.message, stack: undefined }
      const report = buildReport(err.message ?? String(event.message), err.stack)
      dispatchReport(report)
      if (process.env.NODE_ENV === "development") {
        const framesFormatted = report.frames.slice(0, 10).map((f) => formatFrame(f))
        persistCrashReport({
          message: report.message,
          stack: report.stack,
          route: report.route,
          buildId: report.buildId,
          suspect: report.suspectFormatted,
          frames: framesFormatted,
          userId: null,
        }).then((id) => {
          if (id) window.dispatchEvent(new CustomEvent(CRASH_REPORT_EVENT, { detail: { ...report, documentId: id } }))
        })
      }
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const err = event.reason
      const message = err instanceof Error ? err.message : String(err)
      const stack = err instanceof Error ? err.stack : undefined
      const report = buildReport(message, stack)
      dispatchReport(report)
      if (process.env.NODE_ENV === "development") {
        const framesFormatted = report.frames.slice(0, 10).map((f) => formatFrame(f))
        persistCrashReport({
          message: report.message,
          stack: report.stack,
          route: report.route,
          buildId: report.buildId,
          suspect: report.suspectFormatted,
          frames: framesFormatted,
          userId: null,
        }).then((id) => {
          if (id) window.dispatchEvent(new CustomEvent(CRASH_REPORT_EVENT, { detail: { ...report, documentId: id } }))
        })
      }
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])

  return null
}
